import { useNavigate } from "react-router-dom";
import { Calculator, Upload, Sparkles, Zap, Award, BadgePercent, Headset, RotateCcw, FileText, ArrowRight } from "lucide-react";

export const ToolPromos = () => {
  const navigate = useNavigate();
  const cards = [
    { id: "ai", icon: Sparkles, title: "AI Material Recommender", desc: "Describe your project, get an exact shoppable materials list", cta: "Try now", dark: true },
    { id: "calculator", icon: Calculator, title: "Material Calculator", desc: "Enter room size, get quantities with live vendor prices", cta: "Calculate" },
    { id: "bom", icon: Upload, title: "Bulk Order Builder", desc: "Upload your BOM / CSV, auto-fill your cart in seconds", cta: "Upload" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10" data-testid="tool-promos">
      <div className="grid md:grid-cols-4 gap-4">
        <button onClick={() => navigate("/buyer?category=laminates")} data-testid="promo-laminates"
          className="text-left rounded-2xl p-6 bg-orange-700 text-white shadow-lg hover:bg-orange-800 transition-colors">
          <span className="text-[10px] font-mono2 uppercase tracking-[0.2em] text-orange-200">Launch offer</span>
          <h3 className="font-display text-xl font-bold mt-2 leading-tight">Flat 30% off all Laminates</h3>
          <p className="text-xs text-orange-100 mt-2">Merino, Century & Greenlam included</p>
          <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold">Shop laminates <ArrowRight className="h-3 w-3" /></span>
        </button>
        {cards.map((c) => (
          <button key={c.id} onClick={() => navigate(`/tools?tab=${c.id}`)} data-testid={`promo-tool-${c.id}`}
            className={`text-left rounded-2xl p-6 shadow-lg border transition-transform hover:-translate-y-0.5 ${c.dark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-slate-200"}`}>
            <c.icon className={`h-6 w-6 ${c.dark ? "text-amber-400" : "text-orange-700"}`} />
            <h3 className="font-display text-base font-bold mt-3">{c.title}</h3>
            <p className={`text-xs mt-1.5 leading-relaxed ${c.dark ? "text-slate-300" : "text-slate-500"}`}>{c.desc}</p>
            <span className={`mt-4 inline-flex items-center gap-1 text-xs font-bold ${c.dark ? "text-amber-400" : "text-orange-700"}`}>{c.cta} <ArrowRight className="h-3 w-3" /></span>
          </button>
        ))}
      </div>
    </section>
  );
};

export const CatalogGroups = ({ categories, groups }) => {
  const navigate = useNavigate();
  return (
    <section className="bg-white border-y border-slate-200" id="catalog">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="overline-label">Our catalog</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-3">Complete material catalog</h2>
        <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl">{categories.length} categories across {groups.length} groups — every SKU standardized with specs, MOQ and live stock from verified Bengaluru vendors.</p>
        <div className="mt-12 space-y-12">
          {groups.map((g) => {
            const cats = categories.filter((c) => c.group === g.id);
            return (
              <div key={g.id} data-testid={`catalog-group-${g.id}`}>
                <div className="flex items-baseline gap-3 mb-5">
                  <h3 className="font-display text-lg font-semibold text-slate-900">{g.name}</h3>
                  <span className="text-xs text-slate-500 font-mono2">{cats.length} categories</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {cats.map((c) => (
                    <button key={c.id} data-testid={`category-card-${c.id}`} onClick={() => navigate(`/buyer?category=${c.id}`)}
                      className="group text-left rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden hover:border-primary/40 hover:shadow-md transition-all">
                      <div className="h-28 overflow-hidden">
                        <img src={c.image} alt={c.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div className="p-3">
                        <p className="font-semibold text-sm text-slate-900">{c.name}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{c.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const HowItWorks = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" id="how">
    <p className="overline-label">Simple process</p>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-3">From site need to delivery in four steps</h2>
    <div className="mt-12 grid md:grid-cols-4 gap-6">
      {[
        ["Browse & choose", "Search 18 categories by spec, brand, thickness and finish. Or let the AI recommender build the list."],
        ["Add to cart", "Single-vendor baskets with MOQ checks. Live stock from the nearest approved vendor."],
        ["Pick delivery mode", "See exact ETA before paying — 90-min express where feasible, scheduled freight everywhere else."],
        ["Track & build", "SLA countdowns, vendor acceptance alerts and a GST invoice the moment it's delivered."],
      ].map(([t, d], i) => (
        <div key={t} className="relative card-surface p-6" data-testid={`how-step-${i + 1}`}>
          <span className="font-display text-4xl font-bold text-amber-500/80">0{i + 1}</span>
          <h3 className="font-display font-semibold text-slate-900 mt-3">{t}</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

export const Advantages = () => (
  <section className="bg-slate-900 text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <p className="overline-label !text-amber-400">Our advantages</p>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-3">Why interior firms switch to MaterialXpress</h2>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {[
          [Zap, "90-minute express", "Hyperlocal vendors in Peenya, Indiranagar, Whitefield & Electronic City. Honest ETA, enforced SLA."],
          [Award, "Top brands only", "CenturyPly, Greenply, Merino, Hettich, Hafele, Asian Paints, Saint-Gobain, Fevicol and 50+ more."],
          [BadgePercent, "Trade pricing", "Direct distributor partnerships mean wholesale rates. Bulk discounts up to 25% on project orders."],
          [Headset, "Material experts", "Confused between BWP and BWR? Our estimators guide spec choices 7 days a week."],
          [RotateCcw, "Easy returns & reorders", "Approved substitutions, hassle-free returns, and one-tap reorder from history."],
          [FileText, "GST invoices", "CGST + SGST compliant invoice per vendor and order, downloadable any time."],
        ].map(([Icon, t, d]) => (
          <div key={t} data-testid={`advantage-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-800"><Icon className="h-5 w-5 text-amber-400" /></span>
            <h3 className="font-display font-semibold mt-4">{t}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Testimonials = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="testimonials">
    <p className="overline-label">Testimonials</p>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-3">Trusted by Bengaluru's interior teams</h2>
    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[
        ["Rajesh Kumar", "Interior Designer, Indiranagar", "RK", "Materials arrive within the SLA window and the ETA is shown before I pay. No more idle carpenters waiting for hinges."],
        ["Priya Sharma", "Homeowner, Whitefield", "PS", "Renovating my apartment was easier — paints, laminates and hardware all delivered the same afternoon."],
        ["Amit Patel", "Turnkey Contractor, Peenya", "AP", "Bulk pricing is unbeatable and express saves me lakhs every month in project delays."],
        ["Sneha Reddy", "Architect, Koramangala", "SR", "Finally a platform that understands specs. Standardized SKUs, honest stock, and a GST invoice every time."],
      ].map(([n, r, ini, q]) => (
        <figure key={n} className="card-surface p-6 flex flex-col">
          <blockquote className="text-sm text-slate-700 leading-relaxed flex-1">“{q}”</blockquote>
          <figcaption className="mt-5 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-amber-400">{ini}</span>
            <div><p className="text-sm font-semibold text-slate-900">{n}</p><p className="text-xs text-slate-500">{r}</p></div>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

export const FinalCta = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
      <div className="rounded-3xl bg-orange-700 text-white p-10 sm:p-16 grid lg:grid-cols-[1fr_auto] gap-8 items-center" data-testid="final-cta">
        <div>
          <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-orange-200">Now live across 5 Bengaluru zones</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-3">Ready to keep your site moving?</h2>
          <p className="mt-3 text-sm sm:text-base text-orange-100 max-w-xl">Join interior firms and contractors who source plywood, laminates, hardware and finishes with exact ETAs and zero overpromising.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => navigate("/buyer")} data-testid="cta-start-shopping" className="rounded-lg bg-white px-6 py-3 text-sm font-bold text-orange-800 hover:bg-orange-50">Start shopping</button>
          <button onClick={() => navigate("/tools?tab=ai")} data-testid="cta-try-ai" className="rounded-lg border border-white/60 px-6 py-3 text-sm font-bold hover:bg-white/10">Try AI recommender</button>
        </div>
      </div>
    </section>
  );
};

export const Footer = ({ categories }) => (
  <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
      <div>
        <p className="font-display font-bold text-lg text-slate-900">Material<span className="text-orange-700">Express</span></p>
        <p className="text-sm text-slate-500 mt-3 leading-relaxed">Bengaluru's B2B interior-material marketplace. Express where feasible, scheduled everywhere else.</p>
      </div>
      <div>
        <p className="overline-label">Catalog</p>
        <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
          {categories.slice(0, 7).map((c) => <li key={c.id}><a href={`/buyer?category=${c.id}`} className="hover:text-orange-700">{c.name}</a></li>)}
        </ul>
      </div>
      <div>
        <p className="overline-label">Tools</p>
        <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
          <li><a href="/tools?tab=ai" className="hover:text-orange-700">AI Material Recommender</a></li>
          <li><a href="/tools?tab=calculator" className="hover:text-orange-700">Material Calculator</a></li>
          <li><a href="/tools?tab=bom" className="hover:text-orange-700">Bulk Order Builder</a></li>
          <li><a href="/#onboarding" className="hover:text-orange-700">Vendor onboarding</a></li>
        </ul>
      </div>
      <div>
        <p className="overline-label">Contact</p>
        <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
          <li>procurement@materialexpress.in</li>
          <li>+91 90000 00000</li>
          <li>Peenya · Indiranagar · Whitefield · Electronic City · Jigani</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-slate-100 py-4 text-center text-xs text-slate-400">© 2026 MaterialXpress Bengaluru · GST-compliant B2B sourcing</div>
  </footer>
);
